/**
 * 3D Audio Visualization Component
 * Adapted from Google's live-audio example for React integration with Flowcus
 * @license Apache-2.0
 */

import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader';
import './AudioVisualization3D.scss';

interface AudioAnalyser {
  update(): void;
  data: Uint8Array;
}

class Analyser implements AudioAnalyser {
  private analyser: AnalyserNode;
  private bufferLength = 0;
  private _data: Uint8Array;

  constructor(node: AudioNode) {
    this.analyser = node.context.createAnalyser();
    this.analyser.fftSize = 32;
    this.bufferLength = this.analyser.frequencyBinCount;
    this._data = new Uint8Array(this.bufferLength);
    node.connect(this.analyser);
  }

  update() {
    this.analyser.getByteFrequencyData(this._data as any);
  }

  get data() {
    return this._data;
  }
}

// Vertex shader for the background
const backdropVertexShader = `#version 300 es
in vec3 position;
uniform vec2 resolution;
uniform float rand;
out vec3 vPosition;

void main() {
  vPosition = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

// Fragment shader for the background
const backdropFragmentShader = `#version 300 es
precision highp float;
uniform vec2 resolution;
uniform float rand;
in vec3 vPosition;
out vec4 fragColor;

void main() {
  vec2 uv = gl_FragCoord.xy / resolution;
  vec3 color = vec3(0.06, 0.05, 0.08); // Dark purple base
  
  // Add some subtle noise
  float noise = sin(vPosition.x * 20.0 + rand) * sin(vPosition.y * 20.0 + rand) * 0.1;
  color += noise * 0.05;
  
  fragColor = vec4(color, 1.0);
}`;

// Vertex shader for the sphere with audio distortion
const sphereVertexShader = `
varying vec3 vPosition;
varying vec3 vNormal;
uniform float time;
uniform vec4 inputData;
uniform vec4 outputData;

void main() {
  vPosition = position;
  vNormal = normal;
  
  // Audio-reactive distortion
  vec3 newPosition = position;
  float audioIntensity = inputData.x + outputData.x;
  newPosition += normal * sin(time + position.x * 5.0) * 0.1 * audioIntensity;
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
}`;

export interface AudioVisualization3DProps {
  inputNode?: AudioNode;
  outputNode?: AudioNode;
  className?: string;
}

export const AudioVisualization3D: React.FC<AudioVisualization3DProps> = ({
  inputNode,
  outputNode,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const composerRef = useRef<EffectComposer | null>(null);
  const sphereRef = useRef<THREE.Mesh | null>(null);
  const backdropRef = useRef<THREE.Mesh | null>(null);
  const inputAnalyserRef = useRef<Analyser | null>(null);
  const outputAnalyserRef = useRef<Analyser | null>(null);
  const animationFrameRef = useRef<number>(0);
  const rotationRef = useRef(new THREE.Vector3(0, 0, 0));
  const prevTimeRef = useRef(0);

  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize Three.js scene
  const initThreeJS = () => {
    if (!canvasRef.current || !containerRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0a); // Very dark background
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      75,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(2, -2, 5);
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0); // Transparent background
    rendererRef.current = renderer;

    // Backdrop (animated background)
    const backdropGeometry = new THREE.IcosahedronGeometry(10, 5);
    const backdropMaterial = new THREE.RawShaderMaterial({
      uniforms: {
        resolution: { value: new THREE.Vector2(
          containerRef.current.clientWidth * renderer.getPixelRatio(),
          containerRef.current.clientHeight * renderer.getPixelRatio()
        )},
        rand: { value: 0 },
      },
      vertexShader: backdropVertexShader,
      fragmentShader: backdropFragmentShader,
      glslVersion: THREE.GLSL3,
      side: THREE.BackSide
    });
    const backdrop = new THREE.Mesh(backdropGeometry, backdropMaterial);
    scene.add(backdrop);
    backdropRef.current = backdrop;

    // Main sphere
    const sphereGeometry = new THREE.IcosahedronGeometry(1, 10);
    const sphereMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a90e2, // Flowcus blue
      metalness: 0.7,
      roughness: 0.2,
      emissive: 0x1a3a5c,
      emissiveIntensity: 0.5,
    });

    // Add custom shader for audio reactivity
    sphereMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.time = { value: 0 };
      shader.uniforms.inputData = { value: new THREE.Vector4() };
      shader.uniforms.outputData = { value: new THREE.Vector4() };
      
      (sphereMaterial as any).userData = { shader };
      shader.vertexShader = sphereVertexShader;
    };

    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.visible = true;
    scene.add(sphere);
    sphereRef.current = sphere;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x4a90e2, 0.3);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    // Post-processing
    const composer = new EffectComposer(renderer);
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(containerRef.current.clientWidth, containerRef.current.clientHeight),
      1.5, // strength
      0.4, // radius
      0.85 // threshold
    );
    composer.addPass(bloomPass);

    const fxaaPass = new ShaderPass(FXAAShader);
    composer.addPass(fxaaPass);

    composerRef.current = composer;

    setIsInitialized(true);
  };

  // Animation loop
  const animate = () => {
    if (!composerRef.current || !sphereRef.current || !backdropRef.current || !cameraRef.current) {
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    // Update analysers
    inputAnalyserRef.current?.update();
    outputAnalyserRef.current?.update();

    const t = performance.now();
    const dt = (t - prevTimeRef.current) / (1000 / 60);
    prevTimeRef.current = t;

    // Update backdrop
    const backdropMaterial = backdropRef.current.material as THREE.RawShaderMaterial;
    backdropMaterial.uniforms.rand.value = Math.random() * 10000;

    // Update sphere with audio data
    const sphereMaterial = sphereRef.current.material as THREE.MeshStandardMaterial;
    const userData = (sphereMaterial as any).userData;

    if (userData?.shader) {
      const inputData = inputAnalyserRef.current?.data || new Uint8Array(16);
      const outputData = outputAnalyserRef.current?.data || new Uint8Array(16);

      // Scale sphere based on audio
      const outputScale = (outputData[1] || 0) / 255;
      sphereRef.current.scale.setScalar(1 + 0.3 * outputScale);

      // Rotate camera based on audio
      const f = 0.002;
      rotationRef.current.x += (dt * f * 0.5 * outputData[1]) / 255;
      rotationRef.current.z += (dt * f * 0.5 * inputData[1]) / 255;
      rotationRef.current.y += (dt * f * 0.25 * (inputData[2] + outputData[2])) / 255;

      const euler = new THREE.Euler(
        rotationRef.current.x,
        rotationRef.current.y,
        rotationRef.current.z
      );
      const quaternion = new THREE.Quaternion().setFromEuler(euler);
      const cameraPosition = new THREE.Vector3(0, 0, 5);
      cameraPosition.applyQuaternion(quaternion);
      cameraRef.current.position.copy(cameraPosition);
      cameraRef.current.lookAt(sphereRef.current.position);

      // Update shader uniforms
      userData.shader.uniforms.time.value += (dt * 0.1 * outputData[0]) / 255;
      userData.shader.uniforms.inputData.value.set(
        (inputData[0] || 0) / 255,
        (inputData[1] || 0) / 255 * 0.1,
        (inputData[2] || 0) / 255 * 10,
        0
      );
      userData.shader.uniforms.outputData.value.set(
        (outputData[0] || 0) / 255 * 2,
        (outputData[1] || 0) / 255 * 0.1,
        (outputData[2] || 0) / 255 * 10,
        0
      );
    }

    composerRef.current.render();
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  // Handle resize
  const handleResize = () => {
    if (!containerRef.current || !rendererRef.current || !cameraRef.current || !composerRef.current) return;

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    cameraRef.current.aspect = width / height;
    cameraRef.current.updateProjectionMatrix();

    rendererRef.current.setSize(width, height);
    composerRef.current.setSize(width, height);

    // Update backdrop resolution
    if (backdropRef.current) {
      const backdropMaterial = backdropRef.current.material as THREE.RawShaderMaterial;
      const dPR = rendererRef.current.getPixelRatio();
      backdropMaterial.uniforms.resolution.value.set(width * dPR, height * dPR);
    }
  };

  // Initialize
  useEffect(() => {
    initThreeJS();
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      rendererRef.current?.dispose();
    };
  }, []);

  // Setup audio analysers
  useEffect(() => {
    if (inputNode) {
      inputAnalyserRef.current = new Analyser(inputNode);
    }
  }, [inputNode]);

  useEffect(() => {
    if (outputNode) {
      outputAnalyserRef.current = new Analyser(outputNode);
    }
  }, [outputNode]);

  // Start animation
  useEffect(() => {
    if (isInitialized) {
      animate();
      window.addEventListener('resize', handleResize);
      handleResize(); // Initial resize
      
      return () => {
        window.removeEventListener('resize', handleResize);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  return (
    <div 
      ref={containerRef} 
      className={`audio-visualization-3d ${className}`}
    >
      <canvas 
        ref={canvasRef}
        className="visualization-canvas"
      />
    </div>
  );
};

export default AudioVisualization3D;